import { Avatar } from "@dropbox/dig-components/avatar";
import { Badge } from "@dropbox/dig-components/badge";
import { Button } from "@dropbox/dig-components/buttons";
import { Card } from "@dropbox/dig-components/card";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Cluster } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { Twinkle2Line } from "@dropbox/dig-icons/assets";
import { getInitials } from "@mirage/shared/account";
import i18n from "@mirage/translations";
import React from "react";

import type { PersonOrgChart } from "@mirage/service-people/types";
import styles from "./GroupedProfilesCard.module.css";

interface GroupedProfilesCardProps {
  teamName: string;
  profiles: PersonOrgChart[];
  primaryProfile?: PersonOrgChart;
  onFollowAllClick?: () => void;
  maxVisibleProfiles?: number;
  groupName?: string;
}

export const GroupedProfilesCard: React.FC<GroupedProfilesCardProps> = ({
  teamName,
  profiles,
  primaryProfile,
  onFollowAllClick,
  groupName,
  maxVisibleProfiles = 4,
}) => {
  const visibleProfiles = profiles.slice(0, maxVisibleProfiles);
  const remainingCount = profiles.length - maxVisibleProfiles;
  const primaryProfileName =
    primaryProfile?.displayName || profiles[0]?.displayName || "";
  const otherCount = profiles.length - 1;

  if (profiles.length === 0) {
    return null;
  }
  return (
    <Card
      borderRadius="Large"
      hasBorders={false}
      className={styles.cardContainer}
    >
      <Card.Content spacing="medium" className={styles.cardContent}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          rowGap="Micro Large"
        >
          {/* Avatar Stack Section */}
          <div className={styles.avatarStackContainer}>
            {visibleProfiles.map((profile, index) => {
              const initials = getInitials(profile.displayName);

              return (
                <div
                  key={profile.displayName}
                  className={`${styles.avatarWrapper} ${styles[`avatar${index + 1}` as keyof typeof styles]}`}
                >
                  <Avatar
                    src={profile.profilePhotoUrl || ""}
                    size="large"
                    alt={profile.displayName}
                    isInactive={profile.isDeactivated}
                    className={styles.avatar}
                  >
                    {initials}
                  </Avatar>
                </div>
              );
            })}

            {/* Remaining count badge */}
            {remainingCount > 0 && (
              <div className={styles.remainingCountBadge}>
                <Badge
                  variant="standard"
                  size="xsmall"
                  className={styles.remainingCountBadgeContent}
                >
                  {remainingCount > 99 ? "99+" : remainingCount}
                </Badge>
              </div>
            )}
          </div>

          {/* Team Info Section */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            rowGap="Micro Small"
          >
            {/* Team Name */}
            <Text size="large" isBold className={styles.teamNameText}>
              {teamName}
            </Text>

            {/* Member Names */}
            <div className={styles.memberNamesContainer}>
              <Text size="small" className={styles.memberNamesText}>
                <span className={styles.primaryName}>{primaryProfileName}</span>
                {profiles.length > 1 && (
                  <>
                    <span> and </span>
                    <span className={styles.otherCount}>
                      {otherCount} {otherCount === 1 ? "other" : "others"}
                    </span>
                  </>
                )}
              </Text>
            </div>

            {/* Similar Job Titles Badge */}
            {groupName && (
              <Cluster gap="Micro XSmall" alignX="center">
                <Cluster.Item>
                  <div className={styles.similarJobTitlesIcon}>
                    <UIIcon src={Twinkle2Line} />
                  </div>
                </Cluster.Item>
                <Cluster.Item>
                  <Text size="small" className={styles.similarJobTitlesText}>
                    {groupName}
                  </Text>
                </Cluster.Item>
              </Cluster>
            )}
          </Box>

          {/* View All Button */}
          {onFollowAllClick && (
            <Card.Actions className={styles.cardActions}>
              <Button
                variant="opacity"
                size="medium"
                onClick={onFollowAllClick}
                className={styles.viewAllButton}
              >
                {i18n.t("grouped_profiles_card_follow_all_button")}
              </Button>
            </Card.Actions>
          )}
        </Box>
      </Card.Content>
    </Card>
  );
};
